import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';
import type { FaceBounds } from '../types';

export type DetectedFace = {
  /** 1-based Person # after row-major assignment */
  index: number;
  /** Normalized 0–1 relative to the analysis preview */
  faceBounds: FaceBounds;
  /** Detection confidence from MediaPipe */
  confidence: number;
  centerX: number;
  centerY: number;
  height: number;
};

type RawFace = Omit<DetectedFace, 'index'>;

const WASM_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';

/** Official MediaPipe Tasks Vision BlazeFace models (float16). */
export const FACE_MODEL_FULL_RANGE =
  'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_full_range/float16/1/blaze_face_full_range.tflite';
export const FACE_MODEL_SHORT_RANGE =
  'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';

/** IoU above this treats two boxes as the same face. */
const IOU_MERGE_THRESHOLD = 0.45;

const detectorsByModel = new Map<string, Promise<FaceDetector>>();
let visionPromise: Promise<Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>> | null =
  null;

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

async function getVision() {
  if (!visionPromise) {
    visionPromise = FilesetResolver.forVisionTasks(WASM_CDN).catch((err) => {
      visionPromise = null;
      throw err;
    });
  }
  return visionPromise;
}

async function createDetector(modelAssetPath: string): Promise<FaceDetector> {
  const vision = await getVision();
  const base = {
    runningMode: 'IMAGE' as const,
    minDetectionConfidence: 0.4,
  };
  try {
    return await FaceDetector.createFromOptions(vision, {
      ...base,
      baseOptions: { modelAssetPath, delegate: 'GPU' },
    });
  } catch {
    return FaceDetector.createFromOptions(vision, {
      ...base,
      baseOptions: { modelAssetPath, delegate: 'CPU' },
    });
  }
}

async function getDetector(modelAssetPath: string): Promise<FaceDetector> {
  let pending = detectorsByModel.get(modelAssetPath);
  if (!pending) {
    pending = createDetector(modelAssetPath).catch((err) => {
      detectorsByModel.delete(modelAssetPath);
      throw err;
    });
    detectorsByModel.set(modelAssetPath, pending);
  }
  return pending;
}

export function boxIoU(a: FaceBounds, b: FaceBounds): number {
  const x1 = Math.max(a.xMin, b.xMin);
  const y1 = Math.max(a.yMin, b.yMin);
  const x2 = Math.min(a.xMax, b.xMax);
  const y2 = Math.min(a.yMax, b.yMax);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  if (inter <= 0) return 0;
  const areaA = Math.max(0, a.xMax - a.xMin) * Math.max(0, a.yMax - a.yMin);
  const areaB = Math.max(0, b.xMax - b.xMin) * Math.max(0, b.yMax - b.yMin);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
}

/**
 * Merge overlapping detections; keep the higher-confidence box for each cluster.
 */
export function mergeDetectionsByIoU(
  faces: RawFace[],
  iouThreshold = IOU_MERGE_THRESHOLD
): RawFace[] {
  if (faces.length <= 1) return faces;
  const ordered = [...faces].sort((a, b) => b.confidence - a.confidence);
  const kept: RawFace[] = [];
  for (const face of ordered) {
    const duplicate = kept.some((k) => boxIoU(k.faceBounds, face.faceBounds) >= iouThreshold);
    if (!duplicate) kept.push(face);
  }
  return kept;
}

/**
 * Group faces into visual rows using vertical-center proximity scaled by median face height,
 * then number top→bottom / left→right within each row.
 */
export function assignPersonIndexes(faces: RawFace[]): DetectedFace[] {
  if (faces.length === 0) return [];

  const medH = median(faces.map((f) => f.height)) || 0.08;
  const rowTol = Math.max(0.035, medH * 0.55);

  const sorted = [...faces].sort((a, b) => a.centerY - b.centerY);
  const rows: RawFace[][] = [];

  for (const face of sorted) {
    const last = rows[rows.length - 1];
    if (!last) {
      rows.push([face]);
      continue;
    }
    const rowMeanY = last.reduce((s, f) => s + f.centerY, 0) / last.length;
    if (Math.abs(face.centerY - rowMeanY) <= rowTol) {
      last.push(face);
    } else {
      rows.push([face]);
    }
  }

  const numbered: DetectedFace[] = [];
  let index = 1;
  for (const row of rows) {
    row.sort((a, b) => a.centerX - b.centerX);
    for (const face of row) {
      numbered.push({ ...face, index });
      index += 1;
    }
  }
  return numbered;
}

function loadImage(imageUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Failed to load image for face detection.'));
    el.src = imageUrl;
  });
}

function detectionsFromResult(
  result: { detections?: Array<{ boundingBox?: { originX: number; originY: number; width: number; height: number }; categories?: Array<{ score?: number }>; keypoints?: unknown }> },
  width: number,
  height: number,
  offsetX = 0,
  offsetY = 0,
  scaleX = 1,
  scaleY = 1
): RawFace[] {
  return (result.detections || [])
    .map((det) => {
      const box = det.boundingBox;
      if (!box) return null;
      // Local crop coords → full-image normalized
      const localXMin = box.originX / width;
      const localYMin = box.originY / height;
      const localXMax = (box.originX + box.width) / width;
      const localYMax = (box.originY + box.height) / height;
      const xMin = clamp01(offsetX + localXMin * scaleX);
      const yMin = clamp01(offsetY + localYMin * scaleY);
      const xMax = clamp01(offsetX + localXMax * scaleX);
      const yMax = clamp01(offsetY + localYMax * scaleY);
      if (xMax <= xMin || yMax <= yMin) return null;
      const confidence =
        det.categories?.[0]?.score ?? (Array.isArray(det.keypoints) ? 0.6 : 0.5);
      return {
        faceBounds: { xMin, yMin, xMax, yMax },
        confidence,
        centerX: (xMin + xMax) / 2,
        centerY: (yMin + yMax) / 2,
        height: yMax - yMin,
      };
    })
    .filter(Boolean) as RawFace[];
}

async function runDetectorOnImage(
  detector: FaceDetector,
  img: HTMLImageElement
): Promise<RawFace[]> {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) return [];
  try {
    const result = detector.detect(img);
    return detectionsFromResult(result, width, height);
  } catch (err) {
    console.warn('Face detection pass failed:', err);
    return [];
  }
}

/**
 * Run full-range + short-range BlazeFace, merge by IoU, assign Person # indexes.
 * Ordinary Kinvera photos: typically 2 local detector passes.
 */
export async function detectFacesInImage(imageUrl: string): Promise<DetectedFace[]> {
  let img: HTMLImageElement;
  try {
    img = await loadImage(imageUrl);
  } catch (err) {
    console.warn('Face detection image load failed:', err);
    return [];
  }

  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) return [];

  const rawBatches: RawFace[][] = [];

  for (const model of [FACE_MODEL_FULL_RANGE, FACE_MODEL_SHORT_RANGE]) {
    try {
      const detector = await getDetector(model);
      rawBatches.push(await runDetectorOnImage(detector, img));
    } catch (err) {
      console.warn(`MediaPipe FaceDetector unavailable (${model}):`, err);
    }
  }

  const merged = mergeDetectionsByIoU(rawBatches.flat());
  return assignPersonIndexes(merged);
}

type CropSpec = { sx: number; sy: number; sw: number; sh: number };

function overlappingCrops(): CropSpec[] {
  // Normalized source rects — halves + center, limited pass count
  return [
    { sx: 0, sy: 0, sw: 0.55, sh: 1 },
    { sx: 0.45, sy: 0, sw: 0.55, sh: 1 },
    { sx: 0, sy: 0, sw: 1, sh: 0.55 },
    { sx: 0, sy: 0.45, sw: 1, sh: 0.55 },
    { sx: 0.2, sy: 0.2, sw: 0.6, sh: 0.6 },
  ];
}

/**
 * Secondary multi-scale fallback: detect on overlapping regions with the full-range model
 * and map boxes back to full-image coordinates. Call when visual analysis reports more
 * people than the primary dual-pass localized.
 *
 * Existing Person # indexes are preserved for overlapping boxes so they stay aligned with
 * the numbered Subject Map Gemini already inspected.
 */
export async function detectFacesMultiScale(
  imageUrl: string,
  existing: DetectedFace[] = []
): Promise<DetectedFace[]> {
  let img: HTMLImageElement;
  try {
    img = await loadImage(imageUrl);
  } catch {
    return existing;
  }

  const fullW = img.naturalWidth || img.width;
  const fullH = img.naturalHeight || img.height;
  if (!fullW || !fullH) return existing;

  let detector: FaceDetector;
  try {
    detector = await getDetector(FACE_MODEL_FULL_RANGE);
  } catch {
    return existing;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return existing;

  const cropFaces: RawFace[] = [];
  for (const crop of overlappingCrops()) {
    const sx = Math.floor(crop.sx * fullW);
    const sy = Math.floor(crop.sy * fullH);
    const sw = Math.max(1, Math.floor(crop.sw * fullW));
    const sh = Math.max(1, Math.floor(crop.sh * fullH));
    canvas.width = sw;
    canvas.height = sh;
    ctx.clearRect(0, 0, sw, sh);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

    try {
      const result = detector.detect(canvas as unknown as HTMLCanvasElement);
      cropFaces.push(
        ...detectionsFromResult(result, sw, sh, crop.sx, crop.sy, crop.sw, crop.sh)
      );
    } catch {
      /* skip failed crop */
    }
  }

  const kept: DetectedFace[] = [...existing];
  const novel: RawFace[] = [];

  for (const face of mergeDetectionsByIoU(cropFaces)) {
    const match = kept.find((k) => boxIoU(k.faceBounds, face.faceBounds) >= IOU_MERGE_THRESHOLD);
    if (match) {
      if (face.confidence > match.confidence) {
        Object.assign(match, {
          faceBounds: face.faceBounds,
          confidence: face.confidence,
          centerX: face.centerX,
          centerY: face.centerY,
          height: face.height,
        });
      }
    } else {
      novel.push(face);
    }
  }

  if (novel.length === 0) return kept;

  // Number only the novel faces among themselves, then offset past existing max index
  const maxExisting = kept.reduce((m, f) => Math.max(m, f.index), 0);
  const numberedNovel = assignPersonIndexes(novel).map((f) => ({
    ...f,
    index: f.index + maxExisting,
  }));

  return [...kept, ...numberedNovel].sort((a, b) => a.index - b.index);
}

/**
 * Full analysis-time detection: dual-pass (full + short) then multi-scale crops,
 * then a single deterministic Person # assignment for the internal Gemini map.
 * Prefer coverage over shaving local inference time during Alpha.
 */
export async function detectFacesForAnalysis(imageUrl: string): Promise<DetectedFace[]> {
  const primary = await detectFacesInImage(imageUrl);
  let combined = primary;
  try {
    combined = await detectFacesMultiScale(imageUrl, primary);
  } catch (err) {
    console.warn('Multi-scale face detection skipped:', err);
  }
  // Fresh row-major numbering after all merges — Gemini has not seen markers yet
  const raw: RawFace[] = combined.map(({ index: _i, ...rest }) => rest);
  return assignPersonIndexes(mergeDetectionsByIoU(raw));
}

/** Build a modest face box centered on a user click (normalized 0–1). */
export function faceBoundsFromClick(
  nx: number,
  ny: number,
  size = 0.09
): FaceBounds {
  const half = size / 2;
  const xMin = clamp01(nx - half);
  const yMin = clamp01(ny - half);
  const xMax = clamp01(nx + half);
  const yMax = clamp01(ny + half);
  return { xMin, yMin, xMax: Math.max(xMin + 0.02, xMax), yMax: Math.max(yMin + 0.02, yMax) };
}
